// src/components/auth/UserProfile.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  FiUser, FiMail, FiPhone, FiMapPin, FiShield, FiSettings, 
  FiEye, FiEyeOff, FiSave, FiEdit, FiCalendar, FiDollarSign,
  FiHelpCircle, FiLock, FiCheckCircle, FiAlertTriangle, FiClock
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import LeaveOverview from '../staff/LeaveOverview';
import StaffDashboard from '../staff/StaffDashboard';
import PayrollSummary from '../staff/PayrollSummary';


const UserProfile = ({ onClose, initialTab = 'overview' }) => {
  const { currentUser, userProfile, updateUserProfile, resetPassword, recordConsent, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDataProtectionAgreement, setShowDataProtectionAgreement] = useState(false);
  const [showDataProtectionPolicy, setShowDataProtectionPolicy] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setEditedProfile({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        phone: userProfile.phone || '',
        dateOfBirth: userProfile.dateOfBirth || '',
        address: {
          street: userProfile.address?.street || '',
          city: userProfile.address?.city || '',
          state: userProfile.address?.state || '',
          zipCode: userProfile.address?.zipCode || ''
        },
        emergencyContact: {
          name: userProfile.emergencyContact?.name || '',
          phone: userProfile.emergencyContact?.phone || ''
        },
        notes: userProfile.notes || ''
      });
    }
  }, [userProfile]);

  // Listen for close profile event from LeaveOverview
  useEffect(() => {
    const handleCloseProfile = () => {
      onClose();
    };

    window.addEventListener('closeUserProfile', handleCloseProfile);
    return () => {
      window.removeEventListener('closeUserProfile', handleCloseProfile);
    };
  }, [onClose]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateUserProfile(editedProfile);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!currentUser?.email) {
      toast.error('No email address found');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(currentUser.email);
      setPasswordResetSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error('Error sending password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentGiven = async (consentType) => {
    try {
      await recordConsent(consentType);
    } catch (error) {
      console.error('Error recording consent:', error);
    }
  };

  const handleDataProtectionAccept = async () => {
    try {
      await recordConsent('dataProtection');
      setShowDataProtectionAgreement(false);
      toast.success('Data Protection Agreement accepted');
    } catch (error) {
      console.error('Error recording data protection consent:', error);
      toast.error('Failed to record consent');
    }
  };

  const hasDataProtectionConsent = userProfile?.dataProtectionConsent?.given;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiUser },
    { id: 'leave', label: 'Leave Overview', icon: FiClock },
    { id: 'appointments', label: 'Appointments', icon: FiCalendar },
    { id: 'payroll', label: 'Payroll', icon: FiDollarSign },
    { id: 'settings', label: 'Settings', icon: FiSettings }
  ];

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <FiUser className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              {userProfile?.firstName} {userProfile?.lastName}
            </h2>
            <p className="text-blue-100 capitalize">{userProfile?.role}</p>
            <p className="text-blue-100 text-sm">{userProfile?.email}</p>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <FiEdit className="w-4 h-4" />
            <span>{isEditing ? 'Cancel' : 'Edit'}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            {isEditing ? (
              <input
                type="text"
                value={editedProfile.firstName}
                onChange={(e) => setEditedProfile(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{userProfile?.firstName || 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            {isEditing ? (
              <input
                type="text"
                value={editedProfile.lastName}
                onChange={(e) => setEditedProfile(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{userProfile?.lastName || 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                value={editedProfile.phone}
                onChange={(e) => setEditedProfile(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{userProfile?.phone || 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
            {isEditing ? (
              <input
                type="date"
                value={editedProfile.dateOfBirth}
                onChange={(e) => setEditedProfile(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <p className="text-gray-900">{userProfile?.dateOfBirth || 'Not set'}</p>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveProfile}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <FiSave className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Professional Development */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Development</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Certifications */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <FiShield className="w-5 h-5 text-blue-600" />
              <h4 className="font-medium text-gray-900">Certifications</h4>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {userProfile?.professionalDevelopment?.certifications?.length || 0}
            </p>
            <p className="text-sm text-gray-600">Active certifications</p>
          </div>

          {/* Courses Completed */}
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <FiCheckCircle className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-gray-900">Courses</h4>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {userProfile?.professionalDevelopment?.coursesCompleted || 0}
            </p>
            <p className="text-sm text-gray-600">Courses completed</p>
          </div>

          {/* Specializations */}
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <FiHelpCircle className="w-5 h-5 text-purple-600" />
              <h4 className="font-medium text-gray-900">Specializations</h4>
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {userProfile?.professionalDevelopment?.specializations?.length || 0}
            </p>
            <p className="text-sm text-gray-600">Areas of expertise</p>
          </div>
        </div>

        {/* Professional Development Details */}
        <div className="mt-6 space-y-4">
          {userProfile?.professionalDevelopment?.certifications?.length > 0 && (
            <div>
              <h5 className="font-medium text-gray-900 mb-2">Recent Certifications</h5>
              <div className="space-y-2">
                {userProfile.professionalDevelopment.certifications.slice(0, 3).map((cert, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{cert.name}</p>
                      <p className="text-sm text-gray-600">{cert.issuer}</p>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(cert.date).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {userProfile?.professionalDevelopment?.specializations?.length > 0 && (
            <div>
              <h5 className="font-medium text-gray-900 mb-2">Specializations</h5>
              <div className="flex flex-wrap gap-2">
                {userProfile.professionalDevelopment.specializations.map((spec, index) => (
                  <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data Protection Compliance */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Protection & Confidentiality</h3>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            {hasDataProtectionConsent ? (
              <FiCheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <FiAlertTriangle className="w-5 h-5 text-yellow-500" />
            )}
            <div>
              <p className="font-medium text-gray-900">Staff Data Protection Agreement</p>
              <p className="text-sm text-gray-600">
                {hasDataProtectionConsent 
                  ? `Accepted on ${new Date(userProfile.dataProtectionConsent.date).toLocaleDateString()}`
                  : 'Required: GDPR & PDPA compliance agreement'
                }
              </p>
            </div>
          </div>
          {!hasDataProtectionConsent ? (
            <button
              onClick={() => setShowDataProtectionAgreement(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Review & Accept
            </button>
          ) : (
            <button
              onClick={() => setShowDataProtectionAgreement(true)}
              className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              View Agreement
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      {/* Password Reset */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Management</h3>
        
        {passwordResetSent ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <FiCheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-green-800">Password reset email sent!</p>
                <p className="text-sm text-green-600 mt-1">
                  Check your email ({currentUser?.email}) for instructions to reset your password.
                </p>
              </div>
            </div>
            <button
              onClick={() => setPasswordResetSent(false)}
              className="mt-3 text-sm text-green-600 hover:text-green-800 underline"
            >
              Send another email
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FiLock className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">Reset Your Password</p>
                  <p className="text-sm text-blue-600 mt-1">
                    We'll send a secure password reset link to your email address: <strong>{currentUser?.email}</strong>
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleSendPasswordReset}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <FiMail className="w-4 h-4" />
              <span>{loading ? 'Sending...' : 'Send Password Reset Email'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Sidebar */}
          <div className="w-64 bg-gray-50 p-4 border-r border-gray-200 flex-shrink-0">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto min-h-0">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'leave' && <LeaveOverview />}
            {activeTab === 'settings' && renderSettingsTab()}
            {activeTab === 'appointments' && <StaffDashboard />}
            {activeTab === 'payroll' && <PayrollSummary />}
          </div>
        </div>

        {/* Data Protection Agreement Modal */}
        {showDataProtectionAgreement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">Staff Data Protection & Confidentiality Agreement</h3>
                  <button
                    onClick={() => setShowDataProtectionAgreement(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-y-auto">
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 font-medium">
                      By accepting, you confirm that you understand and agree to your responsibilities under the EU GDPR and the Singapore PDPA.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <p className="text-gray-700">As a member of staff using HealeHub, you must:</p>

                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">🔒</span>
                        <div>
                          <h4 className="font-semibold text-gray-900">1. Protect Patient & Staff Information</h4>
                          <ul className="mt-2 space-y-1 text-gray-700 text-sm">
                            <li>• Treat all personal and medical information as confidential.</li>
                            <li>• Only access records if it is necessary for your role.</li>
                            <li>• Never share login details, passwords, or system access with others.</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">👩‍⚕️</span>
                        <div>
                          <h4 className="font-semibold text-gray-900">2. Use Data Only for Work Purposes</h4>
                          <ul className="mt-2 space-y-1 text-gray-700 text-sm">
                            <li>• Use client and staff data only for legitimate clinic tasks.</li>
                            <li>• Do not use personal data for personal gain, curiosity, or outside work.</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">📜</span>
                        <div>
                          <h4 className="font-semibold text-gray-900">3. Follow Legal Standards (GDPR & PDPA)</h4>
                          <ul className="mt-2 space-y-1 text-gray-700 text-sm">
                            <li>• <strong>Data minimisation</strong> – only collect and use the data you need.</li>
                            <li>• <strong>Accuracy</strong> – correct mistakes if you see them.</li>
                            <li>• <strong>Storage limitation</strong> – don't keep personal data longer than needed.</li>
                            <li>• <strong>Security</strong> – report any data breaches, suspicious activity, or lost devices immediately.</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">⚖️</span>
                        <div>
                          <h4 className="font-semibold text-gray-900">4. Respect Individual Rights</h4>
                          <ul className="mt-2 space-y-1 text-gray-700 text-sm">
                            <li>• Patients and staff may request access, correction, or deletion of their data.</li>
                            <li>• If you receive such a request, direct it to your clinic manager or Data Protection Officer (DPO).</li>
                          </ul>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">🚫</span>
                        <div>
                          <h4 className="font-semibold text-gray-900">5. Prohibited Actions</h4>
                          <ul className="mt-2 space-y-1 text-gray-700 text-sm">
                            <li>• Do not download or copy medical records unless authorised.</li>
                            <li>• Do not share data on personal devices, messaging apps, or email.</li>
                            <li>• Do not discuss patient information outside of clinical or operational need.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <span className="text-xl">✅</span>
                        <div>
                          <h4 className="font-semibold text-green-800">By clicking "Accept", you confirm that:</h4>
                          <ul className="mt-2 space-y-1 text-green-700 text-sm">
                            <li>• You have read and understood these responsibilities.</li>
                            <li>• You will follow GDPR, PDPA, and your clinic's privacy policies.</li>
                            <li>• You understand that breaches may result in disciplinary action or legal liability.</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <button
                        onClick={() => setShowDataProtectionPolicy(true)}
                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        To view the Data Protection Policy please click here
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200">
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowDataProtectionAgreement(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  {!hasDataProtectionConsent && (
                    <button
                      onClick={handleDataProtectionAccept}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Accept Agreement
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Protection Policy Modal */}
        {showDataProtectionPolicy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-70 p-4">
            <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">HealeHub – Data Protection Policy</h3>
                  <div className="text-sm text-gray-600">
                    <p>Version: 1.1 | Effective: 23 August 2025</p>
                    <p>Approved By: Christopher Heale, Founder</p>
                  </div>
                  <button
                    onClick={() => setShowDataProtectionPolicy(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-y-auto">
                <div className="prose max-w-none space-y-6">
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">1. Purpose</h3>
                    <p className="text-gray-700">
                      This Data Protection Policy sets out the principles, commitments, and practices adopted by HealeHub to protect personal data in compliance with:
                    </p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>The General Data Protection Regulation (EU) 2016/679 (GDPR);</li>
                      <li>The Singapore Personal Data Protection Act 2012 (PDPA);</li>
                      <li>Applicable global data protection and privacy standards.</li>
                    </ul>
                    <p className="text-gray-700">
                      The purpose is to ensure that all client, staff, and patient data processed through HealeHub is managed lawfully, securely, and transparently.
                    </p>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">2. Scope</h3>
                    <p className="text-gray-700">This policy applies to:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>All personal data processed within HealeHub's multi-tenant clinic management platform;</li>
                      <li>All HealeHub staff, contractors, and authorised processors;</li>
                      <li>All clinics (Controllers) that use the HealeHub platform, and their patients, staff, and partners.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">3. Roles and Responsibilities</h3>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-medium text-gray-900">Data Controller</h4>
                        <p className="text-gray-700">Each clinic (customer) remains the Data Controller and determines the purposes and means of processing their clients' and staff's personal data.</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Data Processor</h4>
                        <p className="text-gray-700">HealeHub acts as a Data Processor, processing data only on documented instructions from the Controller.</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">Data Protection Officer (DPO)</h4>
                        <p className="text-gray-700">HealeHub has appointed a Data Protection Officer (DPO) to oversee compliance, handle requests, and act as point of contact for regulators.</p>
                        <p className="text-blue-600">📧 DPO@healehub.com</p>
                      </div>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">4. Data Protection Principles</h3>
                    <p className="text-gray-700">HealeHub commits to the following principles:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li><strong>Lawfulness, Fairness, Transparency</strong> – data processed only on lawful grounds, clearly explained to data subjects.</li>
                      <li><strong>Purpose Limitation</strong> – data used only for clinic management functions.</li>
                      <li><strong>Data Minimisation</strong> – only the minimum personal data necessary is collected.</li>
                      <li><strong>Accuracy</strong> – clinics are responsible for ensuring data is accurate and up-to-date.</li>
                      <li><strong>Storage Limitation</strong> – data retained only as long as necessary (per Retention Policy).</li>
                      <li><strong>Integrity and Confidentiality</strong> – security controls ensure confidentiality, integrity, and availability.</li>
                      <li><strong>Accountability</strong> – HealeHub documents compliance and supports clinic audits.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">5. Lawful Basis for Processing</h3>
                    <p className="text-gray-700">Data processed within HealeHub is based on:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li><strong>Contractual necessity</strong> – provision of clinic management services.</li>
                      <li><strong>Legal obligations</strong> – tax, employment, and healthcare regulations where applicable.</li>
                      <li><strong>Consent</strong> – where explicitly required (e.g., marketing, sensitive health data under GDPR Art. 9).</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">6. Data Subject Rights</h3>
                    <p className="text-gray-700">HealeHub supports Controllers in fulfilling data subject rights, including:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>Right of access (data exports provided within 30 days).</li>
                      <li>Right to rectification.</li>
                      <li>Right to erasure ("Right to be forgotten").</li>
                      <li>Right to restriction of processing.</li>
                      <li>Right to data portability.</li>
                      <li>Right to object.</li>
                      <li>Right not to be subject to automated decision-making.</li>
                    </ul>
                    <p className="text-gray-700">Requests must be made via the Controller (clinic), which will coordinate with HealeHub.</p>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">7. Data Security Measures</h3>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li><strong>Hosting:</strong> Data is hosted on Google Firebase (GCP) with strong access controls.</li>
                      <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.2/1.3) and at rest (AES-256).</li>
                      <li><strong>Access Controls:</strong> Role-based access ensures that only authorised clinic staff (Admin/Clinician) can access personal or medical records.</li>
                      <li><strong>Audit Trails:</strong> All access and modifications are logged.</li>
                      <li><strong>Multi-Tenant Isolation:</strong> Clinics' data is strictly segregated in Firestore using tenant IDs.</li>
                      <li><strong>Sub-Processors:</strong> HealeHub only uses vetted processors: Firebase (Google), SendGrid (email), GoDaddy (domain/email hosting).</li>
                      <li><strong>Regular Testing:</strong> Penetration testing, vulnerability scanning, and incident simulations.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">7a. Session Management and Authentication Controls</h3>
                    <p className="text-gray-700">HealeHub applies the following standards to protect personal and health data during user sessions:</p>
                    
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900">Default Session Persistence</h4>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>By default, sessions end when the browser is closed.</li>
                          <li>"Remember Me" functionality (persistent login) is only available on personal, verified devices with explicit opt-in.</li>
                          <li>Persistent login is prohibited on shared or reception devices.</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900">Idle Timeout</h4>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>Users are automatically logged out after 30 minutes of inactivity on shared devices, or up to 120 minutes on personal devices.</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900">Re-Authentication</h4>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>Users must re-enter their credentials for sensitive actions or after 12–24 hours of continuous login.</li>
                          <li>Sensitive actions include: data exports, patient record downloads, and administrative configuration changes.</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900">Audit Logging</h4>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>Login metadata (user ID, role, tenant ID, IP address, timestamp) is stored in Firestore for accountability.</li>
                          <li>Controllers may access audit logs to support regulatory audits.</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900">Session Revocation</h4>
                        <ul className="list-disc pl-6 text-gray-700">
                          <li>Controllers and HealeHub administrators can revoke user sessions at any time, e.g. staff departure or account compromise.</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Compliance:</strong> These measures are designed to comply with GDPR Art. 5 & 32 and PDPA Sec. 24, ensuring proportionate safeguards against unauthorised access.
                      </p>
                    </div>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">8. Data Retention and Deletion</h3>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>Data is retained only as long as necessary to fulfil contractual and legal obligations.</li>
                      <li>Clinics may request data exports or permanent deletion (per the Retention & Deletion Policy).</li>
                      <li>Deleted data is irreversibly destroyed from live systems and backups within 90 days.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">9. International Transfers</h3>
                    <p className="text-gray-700">Data may be transferred outside the EEA/Singapore where necessary, but only with appropriate safeguards:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>Standard Contractual Clauses (SCCs);</li>
                      <li>Compliance with PDPA cross-border transfer requirements;</li>
                      <li>Sub-processors contractually bound to equivalent protections.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">10. Incident Response and Breach Notification</h3>
                    <p className="text-gray-700">In the event of a data breach:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>HealeHub will notify affected Controllers without undue delay (within 72 hours where GDPR applies).</li>
                      <li>Controllers remain responsible for notifying data subjects and regulators, but HealeHub will assist.</li>
                      <li>Incident handling follows HealeHub's Incident Response Policy.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">11. Staff Responsibilities</h3>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>All staff and contractors must sign and adhere to the Staff Data Protection Agreement.</li>
                      <li>Training is mandatory at onboarding and refreshed every 12 months.</li>
                      <li>Breaches of this policy may result in disciplinary action.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">12. Policy Review</h3>
                    <p className="text-gray-700">This policy will be:</p>
                    <ul className="list-disc pl-6 text-gray-700">
                      <li>Reviewed annually or upon material regulatory change;</li>
                      <li>Updated to reflect evolving legal requirements and best practices.</li>
                    </ul>
                  </section>

                  <section>
                    <h3 className="text-lg font-semibold text-gray-900">13. Contact Information</h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="font-medium text-blue-900">HealeHub – Data Protection Officer (DPO)</p>
                      <p className="text-blue-700">📧 DPO@healehub.com</p>
                    </div>
                  </section>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200">
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowDataProtectionPolicy(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
